/*
  Storm Motion — shared animation layer for the Storm theme.

  Loaded after gsap.min.js, ScrollTrigger.min.js, SplitText.min.js and
  lenis.min.js (all deferred, so order is guaranteed). Provides:

  1. Lenis smooth scrolling (storefront only, skipped in the theme
     editor and for reduced-motion users), wired into GSAP's ticker.
  2. [data-storm-statement]  Word-by-word scroll reveal for oversized
     statement headings (words brighten from ~15% to full opacity,
     staggered, scrubbed by scroll).
  3. [data-storm-backdrop]   Canvas backdrop with optional layers:
     - dots: a quiet grid of dots that twinkle; the rows compress
       toward the vertical center as the section scrolls through
     - glow: a radial gradient rising from the bottom, driven by scroll
     - transition: pins the section while the glow grows until the
       whole canvas washes into the next color scheme's background,
       handing off seamlessly to the following section
  4. [data-header-cascade]   Nested header menus: hover dropdowns that
     fly out to the side for deeper levels, GSAP-animated.

  Sections opt in via data attributes; everything re-initializes on
  shopify:section:load so the theme editor stays live.
*/
(() => {
  'use strict';

  if (!window.gsap) return;
  gsap.registerPlugin(ScrollTrigger, SplitText);
  if (window.ScrambleTextPlugin) gsap.registerPlugin(ScrambleTextPlugin);

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const designMode = window.Shopify && window.Shopify.designMode;

  /* ------------------------------------------------------------------ */
  /* Lenis smooth scroll                                                */
  /* ------------------------------------------------------------------ */

  let lenis = null;
  if (window.Lenis && !reducedMotion.matches && !designMode) {
    lenis = new Lenis({ autoRaf: false, lerp: 0.1, anchors: true });
    lenis.on('scroll', ScrollTrigger.update);
    gsap.ticker.add((time) => lenis.raf(time * 1000));
    gsap.ticker.lagSmoothing(0);
  }

  /* Per-section cleanup registry (theme editor reloads sections) */
  const cleanups = new WeakMap();
  const addCleanup = (root, fn) => {
    if (!cleanups.has(root)) cleanups.set(root, []);
    cleanups.get(root).push(fn);
  };

  const hexToRgb = (hex) => {
    const n = parseInt(hex.replace('#', ''), 16);
    return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
  };

  /* Run `fn` once when `el` first crosses the given ScrollTrigger start.
     Uses ScrollTrigger so Lenis-smoothed scrolling is respected, unlike
     a native window scroll listener. `start` may be a fraction (0.8 →
     "top 80%") or a ScrollTrigger start string. */
  const onceInView = (root, el, start, fn) => {
    const startAt = typeof start === 'string' ? start : `top ${Math.round(start * 100)}%`;
    const st = ScrollTrigger.create({
      trigger: el,
      start: startAt,
      once: true,
      onEnter: fn,
    });
    addCleanup(root, () => st.kill());
  };

  /* ------------------------------------------------------------------ */
  /* Backdrop: dot grid + gradient glow + scheme-wash transition        */
  /* ------------------------------------------------------------------ */

  const DOT_CELL = 112; /* target grid cell in px */
  const DOT_SQUEEZE = 0.3; /* how much row spacing compresses over a full traverse */

  const initBackdrop = (root) => {
    const host = root.querySelector('[data-storm-backdrop]');
    if (!host || host.dataset.motionInit) return null;
    host.dataset.motionInit = 'true';

    /* data-dots is the legacy attribute from before pattern variants */
    const pattern = host.dataset.pattern || (host.dataset.dots === 'true' ? 'dots' : 'none');
    const wantPattern = pattern === 'dots' || pattern === 'crosses' || pattern === 'grid';
    const wantMargins = host.dataset.margins === 'true';
    const wantTransition = host.dataset.transition === 'true';
    const wantGlow = host.dataset.glow === 'true' || wantTransition;
    if (!wantPattern && !wantMargins && !wantGlow) return null;

    const patternColor = hexToRgb(host.dataset.color || host.dataset.dotColor || '#8f8f8f');
    const glowCore = hexToRgb(host.dataset.glowCore || '#edece7');
    const glowTint = hexToRgb(host.dataset.glowColor || '#465178');
    const washColor = hexToRgb(host.dataset.transitionBg || '#000000');

    /* Margin lines sit --page-margin in from every section edge */
    let marginX = null;
    let marginY = null;

    const canvas = document.createElement('canvas');
    canvas.className = 'storm-backdrop__canvas';
    canvas.setAttribute('aria-hidden', 'true');
    host.appendChild(canvas);
    const ctx = canvas.getContext('2d');

    let width = 0;
    let height = 0;
    let glowProgress = reducedMotion.matches && !wantTransition ? 0.65 : 0;
    let dotProgress = 0;
    let washAlpha = 0;
    let rafId = null;
    const startTime = performance.now();

    const draw = (now) => {
      const t = (now - startTime) / 1000;
      ctx.clearRect(0, 0, width, height);

      if (wantGlow && glowProgress > 0.01) {
        /* Radial gradient squashed vertically into an ellipse rising
           from below the bottom edge, like a light source offstage.
           glowProgress may exceed 1 during a transition, where the
           glow keeps growing until the wash takes over. */
        /* A transition pins tall sections with their bottom at the
           viewport bottom, so anchoring to the section bottom keeps the
           glow rising from the visible bottom edge in both modes */
        const breath = reducedMotion.matches ? 1 : 1 + 0.04 * Math.sin(t * 0.45);
        const radius = Math.max(width, Math.min(height, window.innerHeight)) * (0.2 + 0.95 * glowProgress) * breath;
        const alpha = Math.min(1, glowProgress * 1.6);
        const cx = width / 2;
        const cy = height * 1.08;

        ctx.save();
        ctx.translate(cx, cy);
        ctx.scale(1, 0.55);
        const g = ctx.createRadialGradient(0, 0, 0, 0, 0, radius);
        g.addColorStop(0, `rgba(${glowCore}, ${0.65 * alpha})`);
        g.addColorStop(0.32, `rgba(${glowTint}, ${0.55 * alpha})`);
        g.addColorStop(0.7, `rgba(${glowTint}, ${0.18 * alpha})`);
        g.addColorStop(1, `rgba(${glowTint}, 0)`);
        ctx.fillStyle = g;
        ctx.fillRect(-radius, -radius / 0.55, radius * 2, radius * 2 / 0.55);
        ctx.restore();

        /* Without a transition the section's bottom edge can be visible
           mid-viewport, where the clipped glow would draw a hard line.
           Fade the glow out toward the edge, but only as the edge
           actually scrolls into view so the "light from below" look is
           kept while reading. */
        if (!wantTransition) {
          /* Reach full strength while the edge is still just below the
             viewport, so the clip is never visible on screen */
          const edge = host.getBoundingClientRect().bottom;
          const vh = window.innerHeight;
          const maskStrength = Math.min(1, Math.max(0, (vh + 160 - edge) / 140));
          if (maskStrength > 0.01) {
            const fadeH = Math.min(140, height * 0.12);
            const m = ctx.createLinearGradient(0, height - fadeH, 0, height);
            m.addColorStop(0, 'rgba(0, 0, 0, 0)');
            m.addColorStop(1, `rgba(0, 0, 0, ${maskStrength})`);
            ctx.globalCompositeOperation = 'destination-out';
            ctx.fillStyle = m;
            ctx.fillRect(0, height - fadeH, width, fadeH);
            ctx.globalCompositeOperation = 'source-over';
          }
        }
      }

      if (wantPattern) {
        /* Fixed grid layout; horizontal rows drift toward the vertical
           center as the section traverses the viewport. Twinkle phase
           and speed derive from the cell index, so no stored state. */
        const cols = Math.max(1, Math.round(width / DOT_CELL));
        const rows = Math.max(1, Math.round(height / DOT_CELL));
        const cellW = width / cols;
        const cellH = height / rows;
        const squeeze = 1 - DOT_SQUEEZE * (reducedMotion.matches ? 0 : dotProgress);
        const centerY = height / 2;

        ctx.fillStyle = `rgba(${patternColor}, 1)`;
        if (pattern === 'grid') {
          /* Thin full-width/height lines through the same grid centers;
             the grid compresses on both axes as the section scrolls */
          const centerX = width / 2;
          for (let c = 0; c < cols; c++) {
            const x = centerX + (cellW * (c + 0.5) - centerX) * squeeze;
            ctx.globalAlpha = reducedMotion.matches
              ? 0.09
              : 0.07 + 0.04 * Math.sin(t * 0.22 + c * 1.7);
            ctx.fillRect(x - 0.5, 0, 1, height);
          }
          for (let r = 0; r < rows; r++) {
            const y = centerY + (cellH * (r + 0.5) - centerY) * squeeze;
            ctx.globalAlpha = reducedMotion.matches
              ? 0.09
              : 0.07 + 0.04 * Math.sin(t * 0.26 + r * 2.3);
            ctx.fillRect(0, y - 0.5, width, 1);
          }
        } else {
          for (let r = 0; r < rows; r++) {
            const y = centerY + (cellH * (r + 0.5) - centerY) * squeeze;
            for (let c = 0; c < cols; c++) {
              const seed = r * 131 + c * 17;
              const twinkle = reducedMotion.matches
                ? 0.28
                : 0.24 + 0.18 * Math.sin(t * (0.3 + ((seed * 7) % 50) / 100) + (seed % 100) * 0.0628);
              ctx.globalAlpha = Math.max(0.04, twinkle);
              const x = cellW * (c + 0.5);
              if (pattern === 'crosses') {
                ctx.fillRect(x - 3.5, y - 0.5, 7, 1);
                ctx.fillRect(x - 0.5, y - 3.5, 1, 7);
              } else {
                ctx.beginPath();
                ctx.arc(x, y, 1.1, 0, Math.PI * 2);
                ctx.fill();
              }
            }
          }
        }
        ctx.globalAlpha = 1;
      }

      if (wantMargins && marginX) {
        /* Thin frame lines at the content edges — vertical at the side
           margins, horizontal at the top/bottom padding. Each carries a
           soft highlight that sweeps along the line, driven by scroll
           progress with a gentle idle drift on top. */
        const prog = reducedMotion.matches ? 0 : dotProgress;
        const drift = reducedMotion.matches ? 0 : t * 14;

        const frameLine = (x0, y0, x1, y1, index) => {
          const vertical = Math.abs(y1 - y0) > Math.abs(x1 - x0);
          const len = vertical ? y1 - y0 : x1 - x0;
          ctx.globalAlpha = 1;
          ctx.fillStyle = `rgba(${patternColor}, 0.16)`;
          if (vertical) ctx.fillRect(x0 - 0.5, y0, 1, len);
          else ctx.fillRect(x0, y0 - 0.5, len, 1);
          if (reducedMotion.matches) return;

          const segment = Math.min(320, len * 0.35);
          const cycle = len + segment;
          const p = ((((prog * cycle * 1.3 + drift + index * cycle * 0.37) % cycle) + cycle) % cycle) - segment;
          const grad = vertical
            ? ctx.createLinearGradient(0, y0 + p, 0, y0 + p + segment)
            : ctx.createLinearGradient(x0 + p, 0, x0 + p + segment, 0);
          grad.addColorStop(0, `rgba(${patternColor}, 0)`);
          grad.addColorStop(0.5, `rgba(${patternColor}, 0.45)`);
          grad.addColorStop(1, `rgba(${patternColor}, 0)`);
          ctx.fillStyle = grad;
          if (vertical) ctx.fillRect(x0 - 0.5, y0 + p, 1, segment);
          else ctx.fillRect(x0 + p, y0 - 0.5, segment, 1);
        };

        frameLine(marginX[0], 0, marginX[0], height, 0);
        frameLine(marginX[1], 0, marginX[1], height, 1);
        frameLine(0, marginY[0], width, marginY[0], 2);
        frameLine(0, marginY[1], width, marginY[1], 3);
        ctx.globalAlpha = 1;
      }

      if (washAlpha > 0.001) {
        ctx.fillStyle = `rgba(${washColor}, ${Math.min(1, washAlpha)})`;
        ctx.fillRect(0, 0, width, height);
      }
    };

    const resize = () => {
      const rect = host.getBoundingClientRect();
      const dpr = Math.min(window.devicePixelRatio || 1, 1.5);
      width = rect.width;
      height = rect.height;
      canvas.width = Math.round(width * dpr);
      canvas.height = Math.round(height * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      if (wantMargins) {
        const inset = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--page-margin')) || 24;
        marginX = [inset, width - inset];
        marginY = [inset, height - inset];
      }
      draw(performance.now());
    };

    const loop = (now) => {
      draw(now);
      rafId = requestAnimationFrame(loop);
    };
    const startLoop = () => {
      if (rafId === null && !reducedMotion.matches) rafId = requestAnimationFrame(loop);
    };
    const stopLoop = () => {
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
        rafId = null;
      }
    };

    let visible = false;
    const io = new IntersectionObserver(([entry]) => {
      visible = entry.isIntersecting;
      visible ? startLoop() : stopLoop();
    });
    io.observe(host);

    const ro = new ResizeObserver(resize);
    ro.observe(host);
    resize();

    const redraw = () => {
      if (!visible) draw(performance.now());
    };

    /* Pattern compression and margin-line sweeps follow the section
       through the viewport */
    let dotST = null;
    if ((wantPattern || wantMargins) && !reducedMotion.matches) {
      dotST = ScrollTrigger.create({
        trigger: root,
        start: 'top bottom',
        end: 'bottom top',
        scrub: true,
        onUpdate: (self) => {
          dotProgress = self.progress;
          redraw();
        },
      });
    }

    /* Standalone glow (no transition): scrub in as the section enters */
    let glowST = null;
    if (wantGlow && !wantTransition && !reducedMotion.matches) {
      glowST = ScrollTrigger.create({
        trigger: root,
        start: 'top 80%',
        end: 'center 45%',
        scrub: 0.8,
        onUpdate: (self) => {
          glowProgress = self.progress;
          redraw();
        },
      });
    }

    addCleanup(root, () => {
      stopLoop();
      io.disconnect();
      ro.disconnect();
      dotST && dotST.kill();
      glowST && glowST.kill();
      canvas.remove();
    });

    return {
      wantTransition,
      setGlow: (value) => {
        glowProgress = value;
        redraw();
      },
      setWash: (value) => {
        washAlpha = value;
        redraw();
      },
    };
  };

  /* ------------------------------------------------------------------ */
  /* Statement: word-by-word reveal (+ optional pinned transition)      */
  /* ------------------------------------------------------------------ */

  const DIM_OPACITY = 0.15;

  const initStatement = (root, backdrop) => {
    const heading = root.querySelector('[data-storm-statement]');
    if (!heading || heading.dataset.motionInit) return;
    heading.dataset.motionInit = 'true';

    const transition = !!(backdrop && backdrop.wantTransition) && !reducedMotion.matches;

    /* Split after fonts are ready so line wrapping is final */
    document.fonts.ready.then(() => {
      if (!heading.isConnected) return;

      const split = SplitText.create(heading, {
        type: 'words',
        wordsClass: 'statement__word',
        aria: 'auto',
      });
      addCleanup(root, () => split.revert());

      if (reducedMotion.matches) return;

      /* Reveal follows the heading itself through the viewport; in
         transition mode it must finish just before the section pins
         (its bottom reaching the viewport bottom), which the bottom
         padding guarantees */
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: heading,
          start: 'top 88%',
          end: transition ? 'bottom 92%' : 'bottom 42%',
          scrub: 0.7,
        },
      });

      const eyebrow = root.querySelector('[data-storm-statement-eyebrow]');
      if (eyebrow) {
        tl.fromTo(
          eyebrow,
          { opacity: 0, y: 12 },
          { opacity: 1, y: 0, duration: 0.5, ease: 'power3.out' },
          0
        );
      }

      tl.fromTo(
        split.words,
        { opacity: DIM_OPACITY },
        { opacity: 1, duration: 0.6, ease: 'power2.out', stagger: 0.12 },
        0
      );

      /* The link under the heading eases up once the last words arrive */
      const link = root.querySelector('.statement__inner .link-arrow');
      if (link) {
        tl.fromTo(
          link,
          { opacity: 0, y: 16 },
          { opacity: 1, y: 0, duration: 0.9, ease: 'power3.out' },
          '>-0.4'
        );
      }

      addCleanup(root, () => {
        tl.scrollTrigger && tl.scrollTrigger.kill();
        tl.kill();
      });

      if (!transition) return;

      /* Transition: pin the section once it has been read — for
         sections taller than the viewport that is when their bottom
         reaches the viewport bottom — then grow the glow while the
         content fades into the wash, which paints the canvas in the
         next scheme's background for a seamless handoff. */
      const inner = root.querySelector('.statement__inner');
      const state = { glow: 0, wash: 0 };
      const pinTl = gsap.timeline({
        scrollTrigger: {
          trigger: root,
          start: () => (root.offsetHeight > window.innerHeight ? 'bottom bottom' : 'top top'),
          end: '+=150%',
          scrub: 0.5,
          pin: true,
          /* The body is a column flex container, which makes ScrollTrigger
             auto-disable pinSpacing — force it so the pin distance still
             pushes the following sections down */
          pinSpacing: true,
          anticipatePin: 1,
          invalidateOnRefresh: true,
        },
      });

      pinTl.to(state, {
        glow: 1.9,
        duration: 0.65,
        ease: 'power1.in',
        onUpdate: () => backdrop.setGlow(state.glow),
      }, 0);
      /* Finish the wash before the pin releases so scrub smoothing never
         lets a half-washed frame slip past the handoff */
      pinTl.to(state, {
        wash: 1,
        duration: 0.3,
        ease: 'none',
        onUpdate: () => backdrop.setWash(state.wash),
      }, 0.6);
      pinTl.to({}, { duration: 0.1 }, 0.9);

      /* Content opacity = dim × exit. The dim is a slow, visible fade
         to 45% through the late pin phase (the content never vanishes
         while pinned); the exit factor takes it the rest of the way to
         0 only while the section scrolls out and the next section is
         covering the viewport — so the window is never blank. The exit
         factor reads live geometry per scroll tick, which stays
         correct through pinning and editor reloads alike. */
      if (inner) {
        const fadeState = { dim: 1 };
        const applyFade = () => {
          const rect = inner.getBoundingClientRect();
          const vh = window.innerHeight;
          const exit = 1 - Math.max(0, Math.min(1, (0.65 * vh - rect.bottom) / (0.45 * vh)));
          inner.style.opacity = (fadeState.dim * exit).toFixed(3);
        };
        pinTl.to(fadeState, { dim: 0.45, duration: 0.4, ease: 'power1.inOut', onUpdate: applyFade }, 0.55);
        window.addEventListener('scroll', applyFade, { passive: true });
        applyFade();
        addCleanup(root, () => {
          window.removeEventListener('scroll', applyFade);
          inner.style.opacity = '';
        });
      }

      addCleanup(root, () => {
        pinTl.scrollTrigger && pinTl.scrollTrigger.kill();
        pinTl.kill();
      });
    });
  };

  /* ------------------------------------------------------------------ */
  /* Buttons: GSAP-driven hover                                         */
  /* ------------------------------------------------------------------ */

  /* Delegated so buttons inside re-rendered sections need no rebinding.
     The tweens write CSS variables consumed by .button--gsap rules, and
     overwrite:'auto' lets an interrupted hover continue from wherever
     the wipe currently is.

     Scroll can clear CSS :hover without firing pointerout (Lenis /
     trackpad). Color follows --wipe, and we also reverse any wipe that
     is no longer actually hovered. */
  const initButtons = () => {
    if (reducedMotion.matches) return;

    const tagButtons = (scope) => {
      scope.querySelectorAll('.button').forEach((btn) => btn.classList.add('button--gsap'));
    };
    tagButtons(document);

    const wipeIn = (btn) => {
      btn.classList.add('button--gsap');
      gsap.to(btn, {
        '--wipe': 1,
        '--tick-shift': '3px',
        duration: 0.6,
        ease: 'expo.out',
        overwrite: true,
        onComplete: () => {
          if (!btn.matches(':hover')) wipeOut(btn);
        },
      });
    };

    const wipeOut = (btn) => {
      gsap.to(btn, {
        '--wipe': 0,
        '--tick-shift': '0px',
        duration: 0.45,
        ease: 'power2.out',
        overwrite: true,
      });
    };

    const closestButton = (node) =>
      node && node.nodeType === 1 && node.closest ? node.closest('.button') : null;

    document.addEventListener('pointerover', (e) => {
      const btn = closestButton(e.target);
      if (!btn) return;
      if (closestButton(e.relatedTarget) === btn) return;
      wipeIn(btn);
    });

    document.addEventListener('pointerout', (e) => {
      const btn = closestButton(e.target);
      if (!btn) return;
      if (closestButton(e.relatedTarget) === btn) return;
      wipeOut(btn);
    });

    let scrollClearQueued = false;
    const clearStaleHovers = () => {
      scrollClearQueued = false;
      document.querySelectorAll('.button--gsap').forEach((btn) => {
        if (btn.matches(':hover')) return;
        const wipe = Number.parseFloat(getComputedStyle(btn).getPropertyValue('--wipe')) || 0;
        if (wipe > 0.001) wipeOut(btn);
      });
    };

    window.addEventListener(
      'scroll',
      () => {
        if (scrollClearQueued) return;
        scrollClearQueued = true;
        requestAnimationFrame(clearStaleHovers);
      },
      { passive: true }
    );

    document.addEventListener('shopify:section:load', (e) => tagButtons(e.target));
  };

  /* ------------------------------------------------------------------ */
  /* Stats: scramble values on entry                                    */
  /* ------------------------------------------------------------------ */

  const initScramble = (root) => {
    root.querySelectorAll('[data-storm-scramble]').forEach((el, i) => {
      if (el.dataset.motionInit) return;
      el.dataset.motionInit = 'true';
      if (reducedMotion.matches || !window.ScrambleTextPlugin) return;
      const original = el.textContent;
      onceInView(root, el, 0.85, () => {
        gsap.to(el, {
          duration: 1.1,
          delay: i * 0.12,
          scrambleText: { text: original, chars: '0123456789', speed: 0.35 },
        });
      });
    });
  };

  /* ------------------------------------------------------------------ */
  /* Reveal: stagger a section's content in when it enters the viewport */
  /* ------------------------------------------------------------------ */

  const initReveal = (root) => {
    const wrap = root.querySelector('[data-storm-reveal]');
    if (!wrap || wrap.dataset.motionInit) return;
    wrap.dataset.motionInit = 'true';
    if (reducedMotion.matches) return;
    const items = [...wrap.children];
    if (!items.length) return;

    gsap.set(items, { autoAlpha: 0, y: 36 });
    const tween = gsap.to(items, {
      autoAlpha: 1,
      y: 0,
      duration: 1,
      ease: 'power3.out',
      stagger: 0.1,
      paused: true,
      clearProps: 'transform,opacity,visibility',
    });
    /* Trigger on the first content child, not the padded wrapper — otherwise
       the section's top padding crosses the viewport long before the heading
       is visible, and the tween is over by the time you look at it. */
    const st = ScrollTrigger.create({
      trigger: items[0],
      start: 'top 85%',
      once: true,
      onEnter: () => tween.play(),
    });
    addCleanup(root, () => {
      st.kill();
      tween.kill();
      gsap.set(items, { clearProps: 'transform,opacity,visibility' });
    });
  };

  /* ------------------------------------------------------------------ */
  /* Hero: parallax media + content drift                               */
  /* ------------------------------------------------------------------ */

  const initHero = (root) => {
    const hero = root.querySelector('[data-storm-parallax]');
    if (!hero || hero.dataset.motionInit) return;
    hero.dataset.motionInit = 'true';
    if (reducedMotion.matches) return;

    const media = hero.querySelector('.hero__media img, .hero__media svg');
    const content = hero.querySelector('.hero__content');
    const tweens = [];

    if (media) {
      /* Slight overscale keeps the edges covered while the media drifts */
      tweens.push(
        gsap.fromTo(
          media,
          { yPercent: -5, scale: 1.12 },
          {
            yPercent: 7,
            scale: 1.12,
            ease: 'none',
            scrollTrigger: { trigger: hero, start: 'top top', end: 'bottom top', scrub: true },
          }
        )
      );
    }
    if (content) {
      /* The content lags behind the section (classic parallax), so the
         next section slides over it, while a slight scale-down widens
         the margins — no fade */
      tweens.push(
        gsap.to(content, {
          y: () => window.innerHeight * 0.25,
          scale: 0.94,
          transformOrigin: 'center center',
          ease: 'none',
          scrollTrigger: {
            trigger: hero,
            start: 'top top',
            end: 'bottom top',
            scrub: true,
            invalidateOnRefresh: true,
          },
        })
      );
    }

    addCleanup(root, () => {
      tweens.forEach((tw) => {
        tw.scrollTrigger && tw.scrollTrigger.kill();
        tw.kill();
      });
    });
  };

  /* ------------------------------------------------------------------ */
  /* Header: nested cascade menus                                       */
  /* ------------------------------------------------------------------ */

  const OPEN_MS = 55;
  const CLOSE_MS = 140;
  const cascadeTimers = new WeakMap();

  const clearCascadeTimer = (item) => {
    const timer = cascadeTimers.get(item);
    if (timer) window.clearTimeout(timer);
    cascadeTimers.delete(item);
  };

  const setCascadeTimer = (item, fn, ms) => {
    clearCascadeTimer(item);
    cascadeTimers.set(item, window.setTimeout(fn, ms));
  };

  const syncCascadeLock = () => {
    document.querySelectorAll('.header').forEach((header) => {
      header.classList.toggle(
        'header--cascade-open',
        Boolean(header.querySelector('[data-cascade-item][data-open]'))
      );
    });
  };

  const placeCascadePanel = (item, panel) => {
    item.classList.remove('is-flip-inline', 'is-flip-block');
    const dir = item.getAttribute('data-cascade-dir') || 'end';
    const trigger = item.querySelector(':scope > .header-cascade__hit') || item;
    const triggerRect = trigger.getBoundingClientRect();
    const width = panel.offsetWidth;
    const height = panel.offsetHeight;
    const gap = 8;
    const margin = 10;

    if (dir === 'down') {
      if (triggerRect.left + width > window.innerWidth - margin) {
        item.classList.add('is-flip-inline');
      }
      if (triggerRect.bottom + gap + height > window.innerHeight - margin) {
        item.classList.add('is-flip-block');
      }
    } else {
      if (triggerRect.right + gap + width > window.innerWidth - margin) {
        item.classList.add('is-flip-inline');
      }
      if (triggerRect.top + height > window.innerHeight - margin) {
        item.classList.add('is-flip-block');
      }
    }
  };

  const setCascadeExpanded = (item, open) => {
    item.querySelector(':scope > .header-cascade__hit')?.setAttribute('aria-expanded', String(open));
    item
      .querySelector(':scope > .header-cascade__drawer-row [data-cascade-toggle]')
      ?.setAttribute('aria-expanded', String(open));
  };

  const closeCascadeItem = (item, immediate = false) => {
    if (!item.hasAttribute('data-open')) return;
    clearCascadeTimer(item);
    item.querySelectorAll(':scope [data-cascade-item][data-open]').forEach((child) => {
      if (child !== item) closeCascadeItem(child, true);
    });

    const panel = item.querySelector(':scope > [data-cascade-panel]');
    item.removeAttribute('data-open');
    setCascadeExpanded(item, false);
    item.classList.remove('is-flip-inline', 'is-flip-block');

    const finish = () => {
      if (panel) {
        const hits = panel.querySelectorAll(':scope > .header-cascade__item > .header-cascade__hit');
        gsap.killTweensOf(hits);
        gsap.set(hits, { clearProps: 'transform,opacity,visibility' });
        panel.hidden = true;
        gsap.set(panel, { clearProps: 'transform,opacity,visibility,height' });
      }
      syncCascadeLock();
    };

    if (!panel) {
      syncCascadeLock();
      return;
    }

    gsap.killTweensOf(panel);
    if (immediate || reducedMotion.matches) {
      finish();
      return;
    }

    const isDrawer = panel.classList.contains('header-cascade__drawer-panel');
    const dir = item.getAttribute('data-cascade-dir') || 'end';
    if (isDrawer) {
      gsap.to(panel, {
        height: 0,
        duration: 0.28,
        ease: 'power2.in',
        overwrite: 'auto',
        onComplete: finish,
      });
      return;
    }

    gsap.to(panel, {
      autoAlpha: 0,
      x: dir === 'end' ? 8 : 0,
      y: dir === 'down' ? 6 : 0,
      duration: 0.18,
      ease: 'power2.in',
      overwrite: 'auto',
      onComplete: finish,
    });
  };

  const openCascadeItem = (item) => {
    const panel = item.querySelector(':scope > [data-cascade-panel]');
    if (!panel) return;

    const parent = item.parentElement;
    if (parent) {
      [...parent.children].forEach((sib) => {
        if (sib !== item && sib.hasAttribute?.('data-cascade-item')) closeCascadeItem(sib);
      });
    }

    if (item.hasAttribute('data-open')) return;
    clearCascadeTimer(item);
    item.setAttribute('data-open', '');
    setCascadeExpanded(item, true);
    panel.hidden = false;

    const isDrawer = panel.classList.contains('header-cascade__drawer-panel');
    if (isDrawer) {
      if (reducedMotion.matches) {
        gsap.set(panel, { height: 'auto', autoAlpha: 1 });
        syncCascadeLock();
        return;
      }
      gsap.fromTo(
        panel,
        { height: 0, autoAlpha: 1 },
        { height: 'auto', duration: 0.38, ease: 'power2.out', overwrite: 'auto' }
      );
      syncCascadeLock();
      return;
    }

    gsap.set(panel, { autoAlpha: 0, x: 0, y: 0 });
    placeCascadePanel(item, panel);

    const dir = item.getAttribute('data-cascade-dir') || 'end';
    const flipInline = item.classList.contains('is-flip-inline');
    const flipBlock = item.classList.contains('is-flip-block');
    const xFrom = dir === 'end' ? (flipInline ? -12 : 12) : 0;
    const yFrom = dir === 'down' ? (flipBlock ? -10 : 10) : 0;
    const hits = panel.querySelectorAll(':scope > .header-cascade__item > .header-cascade__hit');

    if (reducedMotion.matches) {
      gsap.set(panel, { autoAlpha: 1, x: 0, y: 0 });
      syncCascadeLock();
      return;
    }

    gsap.fromTo(
      panel,
      { autoAlpha: 0, x: xFrom, y: yFrom },
      { autoAlpha: 1, x: 0, y: 0, duration: 0.34, ease: 'power3.out', overwrite: 'auto' }
    );
    if (hits.length) {
      gsap.fromTo(
        hits,
        { autoAlpha: 0, y: 6 },
        { autoAlpha: 1, y: 0, duration: 0.28, stagger: 0.032, ease: 'power2.out', delay: 0.04, overwrite: 'auto' }
      );
    }
    syncCascadeLock();
  };

  const closeCascades = (scope = document) => {
    scope.querySelectorAll('[data-cascade-item][data-open]').forEach((item) => {
      if (!item.parentElement?.closest('[data-cascade-item][data-open]')) {
        closeCascadeItem(item, true);
      }
    });
  };

  const bindCascadeRoot = (root) => {
    if (root.dataset.cascadeBound) return;
    root.dataset.cascadeBound = 'true';
    const mode = root.getAttribute('data-header-cascade');

    if (mode === 'click') {
      root.addEventListener('click', (event) => {
        const btn = event.target.closest('[data-cascade-toggle]');
        if (!btn || !root.contains(btn)) return;
        event.preventDefault();
        const item = btn.closest('[data-cascade-item]');
        if (!item) return;
        if (item.hasAttribute('data-open')) closeCascadeItem(item);
        else openCascadeItem(item);
      });
      return;
    }

    root.querySelectorAll('[data-cascade-item]').forEach((item) => {
      if (!item.querySelector(':scope > [data-cascade-panel]')) return;

      item.addEventListener('pointerenter', () => {
        if (window.matchMedia('(hover: none)').matches) return;
        let node = item;
        while (node) {
          clearCascadeTimer(node);
          node = node.parentElement?.closest('[data-cascade-item]');
        }
        setCascadeTimer(item, () => openCascadeItem(item), OPEN_MS);
      });

      item.addEventListener('pointerleave', (event) => {
        if (item.contains(event.relatedTarget)) return;
        clearCascadeTimer(item);
        setCascadeTimer(item, () => closeCascadeItem(item), CLOSE_MS);
      });

      item.addEventListener('focusin', () => openCascadeItem(item));
      item.addEventListener('focusout', (event) => {
        if (item.contains(event.relatedTarget)) return;
        closeCascadeItem(item);
      });

      item.addEventListener('click', (event) => {
        if (!window.matchMedia('(hover: none)').matches) return;
        if (item.hasAttribute('data-open')) return;
        const hit = item.querySelector(':scope > .header-cascade__hit');
        if (!hit || !hit.contains(event.target)) return;
        event.preventDefault();
        openCascadeItem(item);
      });
    });
  };

  const initHeaderCascade = (scope) => {
    scope.querySelectorAll('[data-header-cascade]').forEach(bindCascadeRoot);
  };

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    closeCascades(document);
  });

  /* ------------------------------------------------------------------ */
  /* Wiring                                                             */
  /* ------------------------------------------------------------------ */

  const initSection = (root) => {
    const backdrop = initBackdrop(root);
    initStatement(root, backdrop);
    initScramble(root);
    initReveal(root);
    initHero(root);
  };

  const initAll = () => {
    initButtons();
    initHeaderCascade(document);
    document
      .querySelectorAll('[data-storm-statement], [data-storm-backdrop], [data-storm-scramble], [data-storm-reveal], [data-storm-parallax]')
      .forEach((el) => {
        const root = el.closest('.shopify-section') || el.parentElement;
        initSection(root);
      });
    const refresh = () => ScrollTrigger.refresh();
    if (document.readyState === 'complete') refresh();
    else window.addEventListener('load', refresh, { once: true });
  };

  const teardown = (root) => {
    (cleanups.get(root) || []).forEach((fn) => fn());
    cleanups.delete(root);
  };

  document.addEventListener('shopify:section:load', (e) => {
    initSection(e.target);
    initHeaderCascade(e.target);
  });
  document.addEventListener('shopify:section:unload', (e) => teardown(e.target));

  window.StormMotion = { initSection, teardown, lenis, closeCascades };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
})();
